import React from 'react';
import Layout from '@/components/Layout';
import PageMeta from '@/components/seo/PageMeta';
import { FAQSchema, AI_GOVERNANCE_FAQS } from '@/components/seo/schemas';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowRight, HelpCircle, BookOpen, Shield, Scale } from 'lucide-react';

const FAQPage: React.FC = () => {
  // Group FAQs by category
  const categoryFAQs = {
    general: AI_GOVERNANCE_FAQS.slice(0, 3),
    course: AI_GOVERNANCE_FAQS.slice(3, 5),
    governance: AI_GOVERNANCE_FAQS.slice(5, 8),
    technical: AI_GOVERNANCE_FAQS.slice(8, 10),
  };

  return (
    <Layout>
      <PageMeta
        title="Frequently Asked Questions About AI Governance for Boards"
        description="Find answers to common questions about AI governance training, board responsibilities, EU AI Act compliance, and how to implement effective AI oversight in your organization."
        canonicalUrl="/faq"
        keywords="AI governance FAQ, board AI training questions, EU AI Act compliance, AI oversight, board directors AI"
      />
      <FAQSchema faqs={AI_GOVERNANCE_FAQS} />

      <div className="container max-w-4xl py-12 px-4">
        {/* Hero Section */}
        <header className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Everything you need to know about AI governance for board directors, our training programs, and how to implement effective AI oversight.
          </p>
        </header>

        {/* Category Navigation */}
        <nav className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12" aria-label="FAQ Categories">
          <a href="#general" className="flex flex-col items-center p-4 bg-secondary/50 rounded-lg hover:bg-secondary transition-colors">
            <HelpCircle className="h-6 w-6 text-primary mb-2" />
            <span className="text-sm font-medium">General</span>
          </a>
          <a href="#course" className="flex flex-col items-center p-4 bg-secondary/50 rounded-lg hover:bg-secondary transition-colors">
            <BookOpen className="h-6 w-6 text-primary mb-2" />
            <span className="text-sm font-medium">Course</span>
          </a>
          <a href="#governance" className="flex flex-col items-center p-4 bg-secondary/50 rounded-lg hover:bg-secondary transition-colors">
            <Shield className="h-6 w-6 text-primary mb-2" />
            <span className="text-sm font-medium">Governance</span>
          </a>
          <a href="#technical" className="flex flex-col items-center p-4 bg-secondary/50 rounded-lg hover:bg-secondary transition-colors">
            <Scale className="h-6 w-6 text-primary mb-2" />
            <span className="text-sm font-medium">Technical</span>
          </a>
        </nav>

        {/* General Questions */}
        <section id="general" className="mb-12">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <HelpCircle className="h-6 w-6 text-primary" />
            General Questions
          </h2>
          <Accordion type="single" collapsible className="space-y-4">
            {categoryFAQs.general.map((faq, index) => (
              <AccordionItem key={index} value={`general-${index}`} className="border rounded-lg px-4">
                <AccordionTrigger className="text-left font-medium">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        {/* Course Questions */}
        <section id="course" className="mb-12">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <BookOpen className="h-6 w-6 text-primary" />
            Course & Certification
          </h2>
          <Accordion type="single" collapsible className="space-y-4">
            {categoryFAQs.course.map((faq, index) => (
              <AccordionItem key={index} value={`course-${index}`} className="border rounded-lg px-4">
                <AccordionTrigger className="text-left font-medium">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        {/* Governance Questions */}
        <section id="governance" className="mb-12">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <Shield className="h-6 w-6 text-primary" />
            Governance & Oversight
          </h2>
          <Accordion type="single" collapsible className="space-y-4">
            {categoryFAQs.governance.map((faq, index) => (
              <AccordionItem key={index} value={`governance-${index}`} className="border rounded-lg px-4">
                <AccordionTrigger className="text-left font-medium">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        {/* Technical Questions */}
        <section id="technical" className="mb-12">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <Scale className="h-6 w-6 text-primary" />
            Technical & Implementation
          </h2>
          <Accordion type="single" collapsible className="space-y-4">
            {categoryFAQs.technical.map((faq, index) => (
              <AccordionItem key={index} value={`technical-${index}`} className="border rounded-lg px-4">
                <AccordionTrigger className="text-left font-medium">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        {/* CTA Section */}
        <section className="bg-primary/5 rounded-2xl p-8 text-center">
          <h2 className="text-2xl font-bold mb-4">Still have questions?</h2>
          <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
            Our team is here to help. Reach out to discuss your organization's specific AI governance needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild>
              <Link to="/contact">
                Contact Us
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/quiz">
                Take Free Assessment
              </Link>
            </Button>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default FAQPage;
